import TableHeader from '../../components/generic/TableHeader';
import { useEffect, useState } from 'react';
import { cachedGet } from '../../lib/requestDedupe';
import DifficultyAnalysisTable from './tables/DifficultyAnalysisTable';
import { Tab, TabsContainer } from '../../utils/SubjectTabs';
import DifficultyAnalysisChart from './charts/DifficultyAnalysisChart';
import ComparativeDifficultyTable from './tables/ComparativeDifficultyTable';
import TestAnalysisLoader from '../../components/loaders/TestAnalysisLoader';

export default function DifficultyAnalysis({
  packId,
  testId,
  testIdTwo,
  isCombined = false,
  isComparative,
  comparativeData,
}) {
  const [data, setData] = useState(null);
  const [subjectMeta, setSubjectMeta] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [comparativeDifficultyData, setComparativeDifficultyData] = useState(null);
  const [userDifficultyData, setUserDifficultyData] = useState(null);
  const [difficultyWiseQuestions, setDifficultyWiseQuestions] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const res = await cachedGet(
          isCombined
            ? `/api/test/combined/${packId}/difficulty-analysis?testIds=${testId},${testIdTwo}`
            : `/api/test/analysis/v2/${packId}/${testId}/difficultyAnalysis`,
        );
        const response = res?.data?.data;
        if (response) {
          setData(response);
          setSubjectMeta(response?.subjectMeta);
          setSelectedSubject(Object.values(response?.subjectMeta)[0]);
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }
    if (!isComparative) fetchData();
  }, []);

  useEffect(() => {
    if (!isComparative || !comparativeData) return undefined;
    setComparativeDifficultyData(comparativeData?.comparativeData?.difficulty_analysis);
    setUserDifficultyData(comparativeData?.userData?.difficulty_analysis);
    const tempDifficultyWiseQs = comparativeData?.difficultyWiseQuestions;
    let subjectMeta = structuredClone(comparativeData?.subjectMeta);
    if (Object.keys(subjectMeta).length > 2) {
      const overall = { Overall: subjectMeta['Overall'] };
      delete subjectMeta['Overall'];
      subjectMeta = { ...overall, ...subjectMeta };
    } else {
      delete subjectMeta['Overall'];
    }
    setSubjectMeta(subjectMeta);
    setSelectedSubject(Object.values(subjectMeta)[0]);

    const overallDifficultyWiseQs = {
      easy: 0,
      moderate: 0,
      tough: 0,
    };

    Object.entries(tempDifficultyWiseQs)?.forEach(([key, item]) => {
      if (key !== 'Overall') {
        overallDifficultyWiseQs.easy += item?.easy || 0;
        overallDifficultyWiseQs.moderate += item?.moderate || 0;
        overallDifficultyWiseQs.tough += item?.tough || 0;
      }
    });

    tempDifficultyWiseQs.Overall = overallDifficultyWiseQs;
    setDifficultyWiseQuestions(tempDifficultyWiseQs);
  }, [isComparative, comparativeData]);

  const handleSubjectChange = (subject) => {
    setSelectedSubject(subject);
  };

  if (isLoading) return <TestAnalysisLoader />;

  if (isComparative && subjectMeta && difficultyWiseQuestions && userDifficultyData && comparativeDifficultyData) {
    return (
      <div className="space-y-8">
        <TableHeader headerTitle={`Easy (${difficultyWiseQuestions.Overall?.easy} Qs)`} headerTitleClass={'border-b-0'}>
          <div className="w-full overflow-x-auto border border-[var(--q3-stroke-light)] border-y-0">
            <TabsContainer color={selectedSubject?.color}>
              {Object.values(subjectMeta)?.map((tab) => (
                <Tab
                  key={tab?._id}
                  isActive={selectedSubject?.title === tab?.title}
                  color={selectedSubject?.title === tab?.title ? selectedSubject?.color : 'var(--q3-neutral-light)'}
                  tabColor={tab?.color}
                  bgColor="var(--q3-surface-dim)"
                  onClick={() => handleSubjectChange(tab)}
                >
                  {tab?.icon && <img src={tab?.icon} alt={tab?.title} className="" />}
                  <p className="mb-0">{tab?.title}</p>
                </Tab>
              ))}
            </TabsContainer>
          </div>
          <ComparativeDifficultyTable
            selectedSubject={selectedSubject}
            comparativeDifficultyData={comparativeDifficultyData}
            userDifficultyData={userDifficultyData}
            difficultyWiseQuestions={difficultyWiseQuestions}
            type="easy"
            comparativeSegmentsMeta={comparativeData?.comparativeSegmentsMeta}
          />
        </TableHeader>
        <TableHeader
          headerTitle={`Moderate (${difficultyWiseQuestions.Overall?.moderate} Qs)`}
          headerTitleClass={'border-b-0'}
        >
          <div className="w-full overflow-x-auto border border-[var(--q3-stroke-light)] border-y-0">
            <TabsContainer color={selectedSubject?.color}>
              {Object.values(subjectMeta)?.map((tab) => (
                <Tab
                  key={tab?._id}
                  isActive={selectedSubject?.title === tab?.title}
                  color={selectedSubject?.title === tab?.title ? selectedSubject?.color : 'var(--q3-neutral-light)'}
                  tabColor={tab?.color}
                  bgColor="var(--q3-surface-dim)"
                  onClick={() => handleSubjectChange(tab)}
                >
                  {tab?.icon && <img src={tab?.icon} alt={tab?.title} className="" />}
                  <p className="mb-0">{tab?.title}</p>
                </Tab>
              ))}
            </TabsContainer>
          </div>
          <ComparativeDifficultyTable
            selectedSubject={selectedSubject}
            comparativeDifficultyData={comparativeDifficultyData}
            userDifficultyData={userDifficultyData}
            difficultyWiseQuestions={difficultyWiseQuestions}
            type="moderate"
            comparativeSegmentsMeta={comparativeData?.comparativeSegmentsMeta}
          />
        </TableHeader>
        <TableHeader
          headerTitle={`Tough (${difficultyWiseQuestions.Overall?.tough} Qs)`}
          headerTitleClass={'border-b-0'}
        >
          <div className="w-full overflow-x-auto border border-[var(--q3-stroke-light)] border-y-0">
            <TabsContainer color={selectedSubject?.color}>
              {Object.values(subjectMeta)?.map((tab) => (
                <Tab
                  key={tab?._id}
                  isActive={selectedSubject?.title === tab?.title}
                  color={selectedSubject?.title === tab?.title ? selectedSubject?.color : 'var(--q3-neutral-light)'}
                  tabColor={tab?.color}
                  bgColor="var(--q3-surface-dim)"
                  onClick={() => handleSubjectChange(tab)}
                >
                  {tab?.icon && <img src={tab?.icon} alt={tab?.title} className="" />}
                  <p className="mb-0">{tab?.title}</p>
                </Tab>
              ))}
            </TabsContainer>
          </div>
          <ComparativeDifficultyTable
            selectedSubject={selectedSubject}
            comparativeDifficultyData={comparativeDifficultyData}
            userDifficultyData={userDifficultyData}
            difficultyWiseQuestions={difficultyWiseQuestions}
            type="tough"
            comparativeSegmentsMeta={comparativeData?.comparativeSegmentsMeta}
          />
        </TableHeader>
      </div>
    );
  }
  if (!isComparative && subjectMeta) {
    return (
      <div className="space-y-8">
        <TableHeader headerTitle={'Overall Analysis'} headerTitleClass={'border-b-0'}>
          <div className="w-full overflow-x-auto border border-[var(--q3-stroke-light)] border-y-0">
            <TabsContainer color={selectedSubject?.color}>
              {Object.values(subjectMeta)?.map((tab) => (
                <Tab
                  key={tab._id}
                  isActive={selectedSubject?.title === tab.title}
                  color={selectedSubject?.title === tab.title ? selectedSubject?.color : 'var(--q3-neutral-light)'}
                  tabColor={tab.color}
                  bgColor="var(--q3-surface-dim)"
                  onClick={() => handleSubjectChange(tab)}
                >
                  {tab.icon && <img src={tab.icon} alt={tab.title} className="" />}
                  <p className="mb-0">{tab.title}</p>
                </Tab>
              ))}
            </TabsContainer>
          </div>
          <DifficultyAnalysisTable data={data} selectedSubject={selectedSubject} />
        </TableHeader>
        <TableHeader headerTitle={'Graphical Analysis'}>
          <div className="w-full overflow-x-auto border border-[var(--q3-stroke-light)] border-y-0">
            <TabsContainer color={selectedSubject?.color}>
              {Object.values(subjectMeta)?.map((tab) => (
                <Tab
                  key={tab._id}
                  isActive={selectedSubject?.title === tab.title}
                  color={selectedSubject?.title === tab.title ? selectedSubject?.color : 'var(--q3-neutral-light)'}
                  tabColor={tab.color}
                  bgColor="var(--q3-surface-dim)"
                  onClick={() => handleSubjectChange(tab)}
                >
                  {tab.icon && <img src={tab.icon} alt={tab.title} className="" />}
                  <p className="mb-0">{tab.title}</p>
                </Tab>
              ))}
            </TabsContainer>
          </div>
          <DifficultyAnalysisChart data={data} selectedSubject={selectedSubject} />
        </TableHeader>
      </div>
    );
  }
}
